/** Public reasoning/1 request contract. These are observable records, never private model thoughts. */
export const REASONING_SCHEMA = "reasoning-engine/1";
export const REASONING_EFFORTS = ["low", "medium", "high", "max"] as const;
export type ReasoningEffort = (typeof REASONING_EFFORTS)[number];

export interface ReasoningCriterion {
  id: string;
  description: string;
  hard: boolean;
  requiresEvidence: boolean;
}
/** Declared relevance direction; outcomes below are independently followed up. */
export interface ReasoningPurpose {
  intentKey: string;
  intentRevision: string;
  intent: {
    in: string;
    toward: string;
    amplitude: number;
    pursuit: "quote" | "expand" | "follow";
    avoid: string[];
    expect: string[];
  };
  objectives: Array<{ id: string; outcome: string; successSignal: string; weight: number }>;
  perspectives?: Array<{ role: string; intent: string; weight: number }>;
}
export interface ReasoningEvidence {
  sourceKind: string;
  sourceId: string;
  revision: string;
  contentDigest: string;
  observedEpoch?: number;
  expiresAt?: string | null;
}
/** One retained record, as `ReasoningRecordView` serializes it (KnowIt.Application/Reasoning/ReasoningContracts.cs). */
export interface ReasoningRecord {
  id: string;
  kind: string;
  logicalId: string;
  revision: number;
  /** The record schema it was written under (`reasoning-engine/1`). */
  schemaVersion: string;
  payloadDigest: string;
  payload: Record<string, unknown>;
  /** The person or worker whose command appended it. */
  actorId: string;
  createdAt: string;
  eligible: boolean;
  ineligibilityReason: string | null;
}
/** `ReasoningSessionView` — the session with every retained record. */
export interface ReasoningSession {
  id: string;
  runId: string;
  workItemId: string | null;
  schemaVersion: string;
  /** The strategy the session was started under (`sequential/1`). */
  strategyVersion: string;
  revision: number;
  dependencyEpoch: number;
  state: string;
  criteriaDigest: string;
  criteria: ReasoningCriterion[];
  contextRecordId: string;
  currentAcceptanceRecordId: string | null;
  deadline?: string | null;
  records: ReasoningRecord[];
}
/** A Canopy source the context pins whose current version differs from the pinned one (`SourceBarrierView`). */
export interface ReasoningSourceBarrier {
  sourceId: string;
  epoch: number;
  tombstone: boolean;
  revision?: string | null;
  contentDigest?: string | null;
}
export interface ReasoningSessionResponse {
  session: ReasoningSession | null;
  sourceBarriers: ReasoningSourceBarrier[];
}
export interface ReasoningReceipt {
  commandId: string;
  sessionId: string;
  sessionRevision: number;
  recordId: string | null;
  status: string;
  reason: string | null;
}
export interface ReasoningBranchReceipt {
  commandId: string;
  sessionId: string;
  sessionRevision: number;
  groupId: string;
  status: string;
  attemptId: string | null;
  assignmentEpoch: number | null;
  recordId: string | null;
}
export interface ReasoningBranchMember {
  memberId: string;
  purpose: string;
  targetSessionKey: string;
  agent: string;
  model: string;
  effort: string;
  tokenReservation: number;
  mandatory?: boolean;
  deadline?: string;
}
/** One attempt of a branch member (`ReasoningBranchAttemptView`). */
export interface ReasoningBranchAttempt {
  id: string;
  status: string;
  assignmentEpoch: number;
  dispatchId?: string;
  attemptNumber?: number;
  targetSessionKey?: string;
  workerSessionKey?: string | null;
  reservedTokens?: number;
  chargedTokens?: number;
  candidateRecordId?: string | null;
  createdAt?: string;
}
/** `ReasoningBranchGroupView` — an independent-branch group with its members and their attempts. */
export interface ReasoningBranchGroup {
  id: string;
  sessionId: string;
  status: string;
  joinMode: string;
  quorum: number;
  contextRecordId: string;
  contextDigest?: string;
  criteriaDigest?: string;
  dependencyEpoch?: number;
  finalReviewReservation?: number;
  joinedCandidateRecordId?: string | null;
  members: Array<{
    memberId: string;
    purpose: string;
    status: string;
    mandatory?: boolean;
    deadline?: string | null;
    attempts: ReasoningBranchAttempt[];
  }>;
}
/**
 * Provider receipt metadata as `ReasoningController.ProviderReceiptMetadata` publishes it: never the
 * prompt or the full response, only identity, token counts and a digest of the retained text.
 */
export interface ReasoningProviderReceipt {
  requestId?: string | null;
  model?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  durationMs?: number | null;
  effortMode?: string | null;
  outputLimit?: number | null;
  truncated?: boolean | null;
  fullTextDigest?: string | null;
  error?: string | null;
  /** The retained receipt could not be parsed. */
  unreadable?: boolean;
}
export interface ReasoningOperation {
  operationId: string;
  sessionId: string;
  status: string;
  candidateRecordId: string | null;
  evaluationRecordId: string | null;
  error: string | null;
  requestedModel: string;
  requestedEffort: string;
  inputTokens: number;
  outputTokens: number;
  thinkerId?: string;
  contextRecordId?: string;
  criteriaDigest?: string;
  dependencyEpoch?: number;
  reservedTokens?: number;
  /** A completed operation is missing a provider receipt. */
  receiptGap?: boolean;
  /** An operation stage is missing its dispatch, receipt, application or settlement event. */
  journalGap?: boolean;
  generation?: ReasoningProviderReceipt | null;
  evaluation?: ReasoningProviderReceipt | null;
  createdAt?: string;
  updatedAt?: string;
}
/** An operation as the trace lists it: the wire row (`id`, `actual*Tokens`) plus the SDK's normalised names. */
export type ReasoningTraceOperation = ReasoningOperation & {
  id: string;
  thinkerId: string;
  reservedTokens: number;
  actualInputTokens: number;
  actualOutputTokens: number;
  receiptGap: boolean;
  journalGap: boolean;
  generation: ReasoningProviderReceipt | null;
  evaluation: ReasoningProviderReceipt | null;
};
export interface ReasoningTraceEvent {
  id: string;
  sequence: number;
  kind: string;
  payload: Record<string, unknown>;
  commandId?: string;
  actorId?: string;
  recordId?: string | null;
  occurredAt?: string;
}
export interface ReasoningTraceManifest {
  scope?: string;
  /** The session's event counter; every sequence up to it should be recorded. */
  eventSequenceHighWatermark: number;
  recordedEventCount: number;
  /** Up to 16 ranges of sequences that are not recorded; `missingRangeCount` is the full count. */
  missingRanges: Array<{ from: number; to: number }>;
  missingRangeCount: number;
  providerReceiptGapCount: number;
  operationJournalGapCount: number;
  responseOmits?: string[];
}
export interface ReasoningContextInput {
  scopeSlug: string;
  query: string;
  requiredNodeIds?: string[];
  /** Digests captured by the caller; each must match a required source before a session starts. */
  requiredSourceDigests?: Record<string, string>;
  requiredAcceptanceIds?: string[];
  model: string;
  modelContextWindow: number;
  outputReserve: number;
  promptReserve?: number;
  optionalHits?: number;
}
export interface ReasoningStartInput extends ReasoningContextInput {
  runId: string;
  sessionId?: string;
  commandId?: string;
  workItemId?: string;
  criteria: ReasoningCriterion[];
  purpose?: ReasoningPurpose;
  deadline?: string;
}
export interface DirectReasoningRequest extends ReasoningContextInput {
  commandId?: string;
  background?: boolean;
  workItemId?: string;
  thinkerId: string;
  effort: ReasoningEffort;
  effortMode?: "budgeted" | "native";
  evaluatorThinkerId?: string;
  evaluatorModel?: string;
  purpose: ReasoningPurpose;
  criteria: ReasoningCriterion[];
}
export interface ReasoningCommandInput {
  sessionId: string;
  expectedSessionRevision: number;
  commandId?: string;
}
/** `GET /api/reasoning.support` — the server's published limits; clients derive their prose and caps from it. */
export interface ReasoningSupport {
  schemaVersion: string;
  strategyVersion: string;
  capabilities: string[];
  executionHost?: string;
  domain?: string;
  flowNodeKind?: string;
  localExecution: {
    effortChoices: ReasoningEffort[];
    provider: string;
    providers?: string[];
    optInField?: string;
    effortModes?: string[];
    budgetedOutputLimits?: Record<ReasoningEffort, number>;
    note?: string;
  };
  /** `maxRecordBytes`, `sessionTokenBudget`, `maxServerOperations`, `maxIndependentBranches`, … */
  limits: Record<string, number>;
  maxReasoningNodesPerFlow: number;
  supportedBranchAgents?: string[];
  unsupportedProfiles?: string[];
  invalidation?: {
    sourceBarriers: string;
    sources: string[];
    adapters: string[];
    externalSources?: {
      maxResponseBytes: number;
      maxTextBytes: number;
      maxFeedEntries: number;
      refresh: string;
      deletion: string;
      feedScope: string;
    };
    note?: string;
  };
}
export interface ReasoningTrace {
  trace: {
    events: ReasoningTraceEvent[];
    nextSequence: number | null;
  };
  traceCoverage: string;
  operations: ReasoningTraceOperation[];
  traceManifest: ReasoningTraceManifest;
}
/**
 * The dispatch payload an IDE window receives for one independent branch
 * (KnowIt.Infrastructure/Reasoning/ReasoningBranchRuntime.cs `CreateAttempt`). The window verifies
 * `contextManifestJson` against `contextDigest` byte for byte before it acknowledges.
 */
export interface ReasoningBranchAssignment {
  surface: "reasoning-branch";
  sessionId: string;
  branchGroupId: string;
  branchAttemptId: string;
  memberId?: string;
  contextRecordId: string;
  contextManifest: Record<string, unknown>;
  contextManifestJson: string;
  contextDigest: string;
  criteriaDigest?: string;
  dependencyEpoch?: number;
  prompt: string;
  model: string;
  effort?: string;
  tokenReservation: number;
  maxOutputTokens: number;
  requiredExecutionProtocol: typeof REASONING_BRANCH_PROTOCOL;
  toolPolicy: "context-only";
  deadline?: string | null;
}
/** A branch runs under execution protocol 2: acknowledge, checkpoint, report. */
export const REASONING_BRANCH_PROTOCOL = 2;
/** A branch result the server retains; longer results are refused by the window before reporting. */
export const REASONING_BRANCH_RESULT_MAX_BYTES = 16 * 1024;
/**
 * The browser journals a reasoning surface keeps for command replay. The VS Code host mirrors
 * exactly these keys into extension state (thinkinghub-vscode/src/work/docitPagesBridge.ts keeps a
 * byte-identical copy of this source; its parity test reads this file).
 */
export const REASONING_RECOVERY_KEY_SOURCE =
  "^docit:(?:reasoning-request:2:[0-9a-z-]+:[0-9a-z-]+|criterion-review:1:[0-9a-f-]{36}|reasoning-run-action:1:[0-9a-f-]{36}|reasoning-artifact:1:[0-9a-f-]{36})$";
export const REASONING_RECOVERY_KEY_PATTERN = new RegExp(REASONING_RECOVERY_KEY_SOURCE, "i");
export interface ReasoningMemoryGraph {
  status: "current" | "lagging" | "unavailable";
  expectedRevision: number;
  projectedRevision: number | null;
  expectedEventSequence: number;
  projectedEventSequence: number | null;
  expectedRecords: number;
  nodes: Array<{
    id: string;
    kind: string;
    label: string;
    revision: number | null;
    digest: string | null;
  }>;
  edges: Array<{
    source: string;
    target: string;
    kind: string;
    revision: string | null;
    digest: string | null;
  }>;
}
export interface FlowPostmortem {
  runId: string;
  artifactId: string;
  version: number;
  status: "queued" | "processing" | "processed" | "failed" | "uncertain";
  dreamerAttemptId?: string | null;
  dreamerLeaseExpiresAt?: string | null;
  dreamerError?: string | null;
  reportedAt: string;
  summary: string;
  outcome: string;
  lessons: string[];
  purpose?: ReasoningPurpose | null;
  objectiveFollowups?: string[] | null;
  dreamerReview?: string | null;
  dreamerObjectiveFollowups?: string[] | null;
}
export interface DreamerExperimentArtifact {
  path: string;
  createdAt: string;
  content: Record<string, unknown> | null;
}
export interface ReasoningRetainedArtifact {
  recordId: string;
  artifactId: string;
  sha256: string;
  bytes: number;
  mediaType: string;
  content: string;
}

export interface ReasoningSearchOptions {
  mode: "tree" | "graph";
  thinkerId: string;
  model: string;
  evaluatorThinkerId: string;
  evaluatorModel: string;
  effort: ReasoningEffort;
  maxCandidates: number;
  width: number;
  maxDepth: number;
}
export interface ReasoningSearchState {
  searchId: string;
  options: ReasoningSearchOptions;
  contextRecordId: string;
  criteriaDigest: string;
  dependencyEpoch: number;
  startRevision: number;
  revision: number;
  status:
    | "searching"
    | "selected-proposal"
    | "not-found-within-limits"
    | "budget-limited"
    | "stopped"
    | "stale"
    | "uncertain"
    | "failed";
  frontier: Array<{ parents: string[]; depth: number; alternative: number }>;
  candidates: Array<{
    recordId: string;
    evaluationRecordId: string;
    depth: number;
    terminal: boolean;
    qualified: boolean;
    contentDigest: string;
    disposition: string;
  }>;
  advanceCommands: string[];
  mergedPairs: string[];
  pending?: {
    commandId: string;
    advanceCommandId: string;
    entry: ReasoningSearchState["frontier"][number];
  } | null;
  selectedCandidateId?: string | null;
  selectedEvaluationId?: string | null;
  error?: string | null;
}

export interface ReasoningSourceImport {
  documentId: string;
  collectionId: string;
  kind: "web-page" | "feed";
  url: string;
  expectedVersion: number;
}
export interface ReasoningExternalSource {
  documentId: string;
  collectionId: string;
  kind: "web-page" | "feed";
  url: string;
  version: number;
  status: "current" | "gone";
  contentDigest: string;
  observedAt: string;
  changed: boolean;
}

export interface ReasoningApi {
  importSource(input: ReasoningSourceImport): Promise<ReasoningExternalSource>;
  getSource(documentId: string): Promise<ReasoningExternalSource>;
  startSearch(
    input: ReasoningCommandInput & { options: ReasoningSearchOptions },
  ): Promise<ReasoningSearchState>;
  advanceSearch(input: { sessionId: string; commandId?: string }): Promise<ReasoningSearchState>;
  getSearch(sessionId: string): Promise<ReasoningSearchState | null>;
  retainArtifact(
    input: ReasoningCommandInput & {
      content: string;
      mediaType?: "text/plain" | "text/markdown" | "application/json";
    },
  ): Promise<ReasoningReceipt>;
  readArtifact(sessionId: string, recordId: string): Promise<ReasoningRetainedArtifact>;
  localCapabilities(thinkerId: string): Promise<{
    model: string;
    supportsThinking: boolean | null;
    effortLevels: string[];
    nativeLevelsVerified: boolean;
    maxOutputTokens: number;
  }>;
  support(): Promise<ReasoningSupport>;
  request(input: DirectReasoningRequest): Promise<
    | {
        runId: string;
        sessionId: string;
        operation: ReasoningOperation;
        session: ReasoningSession;
      }
    | { runId: string; sessionId: string; status: string; error?: string | null }
  >;
  requestStatus(runId: string): Promise<{
    runId: string;
    sessionId: string;
    status: string;
    error: string | null;
    cancelRequested: boolean;
  }>;
  cancelRequest(
    runId: string,
  ): Promise<{ runId: string; sessionId: string; status: string; cancelRequested: boolean }>;
  reconcileRequest(runId: string): Promise<{ operation: ReasoningOperation }>;
  start(input: ReasoningStartInput): Promise<{ receipt: ReasoningReceipt; sessionId: string }>;
  get(sessionId: string): Promise<ReasoningSessionResponse & { session: ReasoningSession }>;
  byRun(runId: string): Promise<ReasoningSessionResponse>;
  trace(sessionId: string, afterSequence?: number, limit?: number): Promise<ReasoningTrace>;
  graph(sessionId: string): Promise<ReasoningMemoryGraph>;
  execute(input: {
    sessionId: string;
    thinkerId: string;
    model: string;
    effort: ReasoningEffort;
    effortMode?: "budgeted" | "native";
    localOnly?: boolean;
    evaluatorThinkerId?: string;
    evaluatorModel?: string;
    commandId?: string;
    branchGroupId?: string;
    method?: { generationInstructions: string; evaluationInstructions: string };
    mlx?: { enableThinking?: boolean; seed?: number };
    temperature?: number;
    /**
     * Continue from this chain entry: the effective chain up to and including it is pinned into
     * the generation prompt (409 `chain-checkpoint-unavailable` when it is not in the chain).
     */
    chainCheckpointId?: string;
  }): Promise<ReasoningOperation>;
  /** `POST /api/reasoning.chain` — the session's reasoning as one ordered, editable chain. */
  chain(sessionId: string): Promise<ReasoningChainView>;
  /** `POST /api/reasoning.chain.revise` — replace, insert or strike one entry; history is kept. */
  reviseChain(input: ReviseReasoningChainInput): Promise<ReasoningReceipt>;
  refresh(input: ReasoningCommandInput & ReasoningContextInput): Promise<ReasoningReceipt>;
  candidate(
    input: ReasoningCommandInput & {
      logicalId: string;
      revision: number;
      content: string;
      rationale: string;
      evidence: ReasoningEvidence[];
      derivedFromRecordIds?: string[];
      evidenceComplete?: boolean;
    },
  ): Promise<ReasoningReceipt>;
  step(
    input: ReasoningCommandInput & {
      kind: string;
      summary: string;
      rationale?: string;
      inputRecordIds?: string[];
      causedByEventIds?: string[];
      evidence?: ReasoningEvidence[];
    },
  ): Promise<ReasoningReceipt>;
  contribute(
    input: ReasoningCommandInput & {
      kind: string;
      content: string;
      rationale: string;
      baseCandidateRecordId?: string;
      evidence?: ReasoningEvidence[];
    },
  ): Promise<ReasoningReceipt>;
  evaluate(
    input: ReasoningCommandInput & {
      candidateRecordId: string;
      contextRecordId: string;
      criteriaDigest: string;
      verdicts: Array<{ criterionId: string; verdict: string; rationale: string }>;
      rationale: string;
    },
  ): Promise<ReasoningReceipt>;
  accept(
    input: ReasoningCommandInput & { candidateRecordId: string; evaluationRecordId: string },
  ): Promise<ReasoningReceipt>;
  assessEvidence(
    input: ReasoningCommandInput & {
      candidateRecordId: string;
      evidence: ReasoningEvidence;
      relation: "supports" | "contradicts";
      rationale: string;
    },
  ): Promise<ReasoningReceipt>;
  proposeReplan(
    input: ReasoningCommandInput & { criteria: ReasoningCriterion[]; rationale: string },
  ): Promise<ReasoningReceipt>;
  applyReplan(
    input: ReasoningCommandInput & { proposalRecordId: string; impactDigest: string },
  ): Promise<ReasoningReceipt>;
  correctSource(
    input: ReasoningCommandInput & {
      sourceNodeId: string;
      expectedSourceEpoch: number;
      /** Mark the source as gone rather than changed. Defaults to false. */
      tombstone?: boolean;
      rationale: string;
    },
  ): Promise<ReasoningReceipt>;
  activateBranches(
    input: ReasoningCommandInput & {
      members: ReasoningBranchMember[];
      joinMode?: "all" | "any" | "quorum";
      quorum?: number;
    },
  ): Promise<ReasoningBranchReceipt>;
  acknowledgeBranch(input: {
    sessionId: string;
    groupId: string;
    attemptId: string;
    workerSessionKey: string;
    leaseToken: string;
    contextRecordId: string;
    contextDigest: string;
    model: string;
    commandId?: string;
  }): Promise<ReasoningBranchReceipt>;
  reportBranch(input: {
    sessionId: string;
    groupId: string;
    attemptId: string;
    assignmentEpoch: number;
    workerSessionKey: string;
    leaseToken: string;
    contextRecordId: string;
    contextDigest: string;
    model: string;
    status: string;
    content?: string;
    rationale?: string;
    inputTokens?: number;
    outputTokens?: number;
    error?: string;
    commandId?: string;
  }): Promise<ReasoningBranchReceipt>;
  retryBranch(
    input: ReasoningCommandInput & {
      groupId: string;
      memberId: string;
      expectedAttemptId: string;
      targetSessionKey: string;
    },
  ): Promise<ReasoningBranchReceipt>;
  joinBranches(input: ReasoningCommandInput & { groupId: string }): Promise<ReasoningBranchReceipt>;
  cancelBranches(
    input: ReasoningCommandInput & { groupId: string; rationale: string },
  ): Promise<ReasoningBranchReceipt>;
  branchGroup(sessionId: string, groupId: string): Promise<ReasoningBranchGroup | null>;
  branchGroups(sessionId: string): Promise<ReasoningBranchGroup[]>;
  followupObjective(
    input: ReasoningCommandInput & {
      objectiveId: string;
      status: "met" | "partially-met" | "unmet" | "unknown";
      observation: string;
      evidence?: ReasoningEvidence[];
    },
  ): Promise<ReasoningReceipt>;
  submitPostmortem(input: {
    runId: string;
    summary: string;
    outcome: string;
    lessons: string[];
    commandId?: string;
  }): Promise<FlowPostmortem>;
  postmortem(runId: string): Promise<FlowPostmortem | null>;
  dreamerInbox(): Promise<FlowPostmortem[]>;
  processPostmortem(input: {
    runId: string;
    thinkerId: string;
    effort: ReasoningEffort;
    effortMode?: "budgeted" | "native";
    retryAttemptId?: string;
    acknowledgeUncertain?: boolean;
  }): Promise<FlowPostmortem>;
  dreamerExperiments(runId: string): Promise<DreamerExperimentArtifact[]>;
  planDreamerExperiment(input: {
    runId: string;
    experimentId: string;
    candidateFlowSlug: string;
    candidateVersion: number;
    hypothesis: string;
    metric: "completion" | "failed-steps" | "duration-ms";
  }): Promise<unknown>;
  measureDreamerExperiment(input: {
    runId: string;
    experimentId: string;
    candidateRunId: string;
  }): Promise<unknown>;
  publishDreamerExperiment(input: { runId: string; experimentId: string }): Promise<unknown>;
  rollbackDreamerExperiment(input: { runId: string; experimentId: string }): Promise<unknown>;
}

// ── The reasoning chain (KnowIt.Application/Reasoning/ReasoningChain.cs) ─────────────────────
/** The three edits a person can make to the chain; each appends a step, none rewrites history. */
export const REASONING_CHAIN_OPS = ["replace", "insert", "strike"] as const;
export type ReasoningChainOp = (typeof REASONING_CHAIN_OPS)[number];
/** The engine's step vocabulary (`ReasoningStepKinds.All`), in the server's order. */
export const REASONING_STEP_KINDS = [
  "interpretation",
  "assumption",
  "alternative",
  "decision",
  "action-intent",
  "tool-request",
  "tool-observation",
  "verification",
  "revision",
] as const;
export type ReasoningStepKind = (typeof REASONING_STEP_KINDS)[number];
export type ReasoningChainEntryKind = "step" | "candidate" | "thinking";
export type ReasoningChainOrigin = "actor-report" | "model-emitted" | "server-operation";
/**
 * One entry of the chain (`ReasoningChainEntry`). `id` is a record GUID for steps and
 * candidates, or `operation:<32 hex>:thinking` for the thinking a model emitted — generated
 * text, never evidence. `position` is the entry's index in the effective chain, -1 when an
 * edit removed or replaced it (or when it is a strike marker).
 */
export interface ReasoningChainEntry {
  id: string;
  kind: ReasoningChainEntryKind;
  stepKind: string | null;
  summary: string;
  rationale: string | null;
  origin: ReasoningChainOrigin;
  createdAt: string;
  position: number;
  recordId: string | null;
  operationId: string | null;
  candidateRecordId: string | null;
  /** The edit step that replaced or struck this entry. */
  supersededByRecordId: string | null;
  struck: boolean;
  /** Set on an edit step: the entry it replaced (or struck). */
  revisionOfId: string | null;
  /** Set on an inserted step: the entry it was placed after (null = inserted at the start). */
  insertedAfterId: string | null;
  eligible: boolean;
  /** The summary was clipped by the server (or the model's thinking was cut short). */
  truncated: boolean;
  evidence: ReasoningEvidence[];
}
/** `ReasoningChainView` — every entry plus the ordered ids that remain after the edits. */
export interface ReasoningChainView {
  sessionId: string;
  sessionRevision: number;
  entries: ReasoningChainEntry[];
  /** Ordered entry ids after replace/insert/strike: what a reader sees as "the chain". */
  effective: string[];
  digest: string;
}
/**
 * `ReviseReasoningChainRequest`. `replace`/`strike` need `targetId` (any entry id, thinking ids
 * included); `insert` takes `afterId` or none to insert at the start; `replace`/`insert` need a
 * `summary` (1–4000 characters) and an optional `stepKind` (default `revision`); `strike` takes
 * an optional `rationale`. Retry the same edit with the same `commandId`.
 */
export interface ReviseReasoningChainInput extends ReasoningCommandInput {
  operation: ReasoningChainOp;
  targetId?: string;
  afterId?: string;
  stepKind?: ReasoningStepKind;
  summary?: string;
  rationale?: string;
  evidence?: ReasoningEvidence[];
}
/** The refusals `reasoning.chain.revise` and a checkpointed `reasoning.execute` answer with. */
export const REASONING_CHAIN_ERRORS = {
  revisionConflict: "session-revision-conflict",
  entrySuperseded: "chain-entry-superseded",
  entryNotFound: "chain-entry-not-found",
  invalidRevision: "invalid-chain-revision",
  checkpointUnavailable: "chain-checkpoint-unavailable",
} as const;
